// WHAT THE TOOL LAYER PUSHED BACK ON — one predicate, one grouping, server and client.
//
// The defect this closes (tab-agent RESEARCH §4.2b): `ToolBlock` badged a call `rejected` only
// when `part.status === 'error' || part.error`. The Signet tool layer answers a refused write
// with an HTTP 422 and a PLAIN-TEXT body, which OpenCode records as an ordinary `output` with
// status `completed` — so 56 of run #43's 57 rejections rendered as successful tool calls.
//
// The tool layer's own accounting (routes/agentTools.ts `countRejection`) is the authority on
// what a pushback IS: a refused WRITE (`propose_*`, `record_fact`, `timeline_add_event`) is a
// pushback and bumps `stats.tool_rejections`; every other 4xx is ordinary searching and bumps
// `stats.tool_lookup_misses` instead. This module states exactly that rule once, so the
// transcript, the run row and the standing paragraph can never disagree about it.
//
// No `pg`, no React — importable from the server and both SPAs.

/** Every tool the Signet layer serves (routes/agentTools.ts). Anything else is a workspace builtin. */
export const SIGNET_TOOLS = [
  'close_pass', 'coverage_report', 'document_get_span', 'document_list', 'facts_list',
  'flag_open_question', 'library_get_span', 'library_search', 'propose_acceptance_criterion',
  'propose_conflict', 'propose_deliverable', 'propose_requirement', 'propose_test_case',
  'record_fact', 'register_get', 'register_search', 'run_progress',
  'timeline_add_event', 'timeline_discharge_commitment', 'timeline_list', 'whoami',
] as const;

const SIGNET_TOOL_SET = new Set<string>(SIGNET_TOOLS);

/** A WRITE — the class whose refusal the design calls a pushback. Mirrors `countRejection`. */
export const isWriteTool = (tool: string): boolean =>
  /^propose_/.test(tool) || tool === 'record_fact' || tool === 'timeline_add_event';

export const isSignetTool = (tool: string): boolean => SIGNET_TOOL_SET.has(tool);

/** Every Signet tool answers success with a JSON object. Anything else in `output` is a refusal. */
const looksLikeSuccess = (output: string): boolean => {
  const head = output.trimStart();
  return head.startsWith('{') || head.startsWith('[');
};

/**
 * Not a refusal: the harness cancelled the call in flight (an abort, a cap). Counting it as a
 * pushback would put the operator's own Stop button in the fault ledger.
 */
const CANCELLED = /^Tool execution aborted/i;

export interface ToolPartLike {
  tool?: string | null;
  status?: string | null;
  output?: string | null;
  error?: string | null;
}

/** The refusal text, or null when the call was not refused. */
export function refusalOf(part: ToolPartLike): string | null {
  const tool = String(part.tool ?? '');
  if (!tool) return null;
  const err = (part.error ?? '').trim();
  if (err) return CANCELLED.test(err) ? null : err;
  const out = (part.output ?? '').trim();
  if (CANCELLED.test(out)) return null;
  if (!out) return part.status === 'error' ? 'The tool call failed with no message.' : null;
  if (!isSignetTool(tool)) return part.status === 'error' ? out : null;
  return looksLikeSuccess(out) ? null : out;
}

/** A refused WRITE. This is what `stats.tool_rejections` counts. */
export const isPushback = (part: ToolPartLike): boolean =>
  isWriteTool(String(part.tool ?? '')) && refusalOf(part) !== null;

/** A refused LOOKUP — ordinary searching. `stats.tool_lookup_misses`, never the flag. */
export const isLookupMiss = (part: ToolPartLike): boolean =>
  isSignetTool(String(part.tool ?? '')) && !isWriteTool(String(part.tool ?? '')) && refusalOf(part) !== null;

/** Doc refs are the VARIABLE in a fault message; the fault is the constant. */
export const normaliseFault = (msg: string): string => msg.replace(/DOC-\d+/g, '§').trim();

export const docRefsIn = (msg: string): string[] => [...new Set(msg.match(/DOC-\d+/g) ?? [])];

/**
 * The FAMILY a fault belongs to — coarser than the message, because the standing paragraph is
 * about the operator's prompt work, not about wording. `quoted_span not found in §` (75) and
 * `quote not found in § — character-for-character` (8) are one fault: the model paraphrased
 * instead of copying. That is the 83 of 91 the research names.
 */
export function faultFamily(msg: string): string {
  if (/character-for-character|not found in|does not occur in/i.test(msg)) {
    return 'quoted_span is not a character-for-character substring of the cited document';
  }
  if (/evidence is required/i.test(msg)) return 'a proposal arrived with no citation at all';
  if (/\btags?\b/i.test(msg)) return 'tags outside the engagement tag pool';
  if (/already exists|duplicate/i.test(msg)) return 'the register already holds it';
  return normaliseFault(msg).replace(/\s+/g, ' ').slice(0, 120);
}

/** One row of the collapsed fault table. */
export interface FaultClass {
  tool: string;
  /** The message with its doc refs normalised out. */
  msg: string;
  n: number;
  /** The refs the collapsed lines named, in the order they were met. */
  docs: string[];
}

/** Collapse raw (tool, message) pairs: identical faults differing only by DOC ref become one. */
export function collapseFaults(raw: Array<{ tool: string; msg: string; n: number }>): FaultClass[] {
  const by = new Map<string, FaultClass>();
  for (const r of raw) {
    const msg = normaliseFault(r.msg).replace(/\s+/g, ' ');
    const key = `${r.tool}|${msg}`;
    let hit = by.get(key);
    if (!hit) {
      hit = { tool: r.tool, msg, n: 0, docs: [] };
      by.set(key, hit);
    }
    hit.n += r.n;
    for (const d of docRefsIn(r.msg)) if (!hit.docs.includes(d)) hit.docs.push(d);
  }
  return [...by.values()].sort((a, b) => b.n - a.n || a.tool.localeCompare(b.tool) || a.msg.localeCompare(b.msg));
}
